import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import StarRating from "@/components/StarRating";

export default function ReviewsSection({ productSlug }) {
  const [data, setData] = useState({ average: 0, count: 0, items: [] });
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/reviews/${productSlug}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [productSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Pick a star rating");
    setSubmitting(true);
    try {
      await api.post("/reviews", { product_slug: productSlug, rating, title, body, name });
      toast.success("Thanks — your review is live");
      setRating(0); setTitle(""); setBody(""); setName("");
      load();
    } catch {
      toast.error("Couldn't submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16 border-t border-white/10 pt-12" data-testid="reviews-section">
      <div className="flex flex-wrap items-baseline gap-4">
        <h2 className="font-display text-3xl font-black tracking-tighter">Reviews</h2>
        <StarRating value={data.average} count={data.count} size={18} />
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_1.3fr]">
        {/* Form */}
        <form onSubmit={submit} className="border border-white/10 bg-[#0d0d0d] p-6 space-y-3 h-fit">
          <div className="font-label text-xs text-[#00FFC2]">/ WRITE A REVIEW</div>
          <div>
            <div className="font-label text-[10px] text-white/40 mb-1">RATING</div>
            <StarRating value={rating} size={22} interactive onChange={setRating} />
          </div>
          <input placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-[#FF5A1F]" />
          <input placeholder="Headline (e.g. 'Best flavour I've tried')" value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-[#FF5A1F]" />
          <textarea rows={4} placeholder="Tell us about it..." required value={body} onChange={(e) => setBody(e.target.value)}
            className="w-full border border-white/15 bg-black px-3 py-2 text-sm outline-none focus:border-[#FF5A1F]" />
          <button disabled={submitting} data-testid="review-submit"
            className="w-full bg-[#FF5A1F] py-2.5 font-label text-xs text-black hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {submitting ? "Posting..." : "Post review"}
          </button>
        </form>

        {/* List */}
        <div className="space-y-4">
          {data.items.length === 0 && (
            <div className="text-sm text-white/40 border border-white/10 p-6">
              No reviews yet. Be the first to share your take.
            </div>
          )}
          {data.items.map((r, i) => (
            <div key={i} className="border border-white/10 bg-[#0d0d0d] p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-display font-bold">{r.name || "Anonymous"}</div>
                  <StarRating value={r.rating} size={12} />
                </div>
                <div className="text-[10px] font-label text-white/40">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                </div>
              </div>
              {r.title && <div className="mt-3 font-display font-bold text-sm">{r.title}</div>}
              <p className="mt-2 text-sm leading-relaxed text-white/70 whitespace-pre-wrap">{r.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
